import { useCallback, useState } from 'react';
import { notificationService } from '@/lib/services/notification.service';
import { downloadFile } from '@/shared/utils/downloadFile';
import { extension as getExtensionFromMimeType } from 'mime-types';
import {
  ArtifactExportStatus,
  createArtifactExport,
  getArtifactExportAsset,
  getArtifactExportLast,
  getArtifactExportStatus,
} from '../../api';
import type { UseArtifactExportOptions, UseArtifactExportReturn } from './useArtifactExport.types';
import { CreateArtifactExportDto } from '@inference.engine/api-client/web';
import { SnackbarKey } from 'notistack';
import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 5000; // 5 seconds
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const MAX_POLL_ATTEMPTS = MAX_POLL_DURATION_MS / POLL_INTERVAL_MS;

/**
 * Hook for handling artifact export workflow.
 * Manages the complete export lifecycle: checking for existing exports, creating new ones if needed,
 * polling for completion status, and downloading the generated assets.
 *
 * Cross-feature dependency on `getFileDownloadUrl` is INJECTED via `options.getFileDownloadUrl`
 * (Springboard dependency injection pattern). The consumer provides the resolver from `@/features/chats`,
 * keeping this slice free of cross-feature OUT.
 *
 * @param artifactId - The ID of the artifact to export
 * @param referenceKey - Optional reference key for a specific export variant (e.g., idea ID)
 * @param options - Configuration including template, fileName, and the required `getFileDownloadUrl` resolver
 * @returns Object containing the download function, loading state, and error state
 *
 * @example
 * ```tsx
 * // Consumer imports getFileDownloadUrl from the chats feature barrel and passes it as a prop.
 * const { download, isDownloading, error } = useArtifactExport(artifactId, 'docx', {
 *   getFileDownloadUrl,
 *   templateName: 'docx',
 *   fileName: 'Copy',
 * });
 * ```
 */
export function useArtifactExport(
  artifactId: string | undefined,
  referenceKey: string | undefined,
  options: UseArtifactExportOptions,
): UseArtifactExportReturn {
  const { t } = useTranslation('common');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { getFileDownloadUrl, fileName, templateName, additionalOptions } = options;

  const downloadAsset = useCallback(
    async (assetId: string, mimeType: string, downloadNotifId: SnackbarKey): Promise<void> => {
      const downloadUrl = await getFileDownloadUrl(assetId);
      /* eslint-disable i18next/no-literal-string -- file extension values */
      const EXTENSION_OVERRIDES: Record<string, string> = { markdown: 'md' };
      const rawExtension = getExtensionFromMimeType(mimeType) || 'pdf';
      /* eslint-enable i18next/no-literal-string */
      const extension = EXTENSION_OVERRIDES[rawExtension] ?? rawExtension;
      await downloadFile({
        url: downloadUrl,
        mimeType: mimeType,
        fileName: fileName ? `${fileName}.${extension}` : `export.${extension}`,
      });
      notificationService.close(downloadNotifId);
      notificationService.showSuccess(
        t('artifacts.useArtifactExport.exportDownloadedSuccessfully'),
      );
      setIsDownloading(false);
    },
    [fileName, getFileDownloadUrl, t],
  );

  const pollExport = useCallback(async (artifactId: string, exportId: string) => {
    const startTime = Date.now();
    let pollAttempts = 0;
    let currentStatus = ArtifactExportStatus.PROCESSING as ArtifactExportStatus;

    while (
      currentStatus === ArtifactExportStatus.PROCESSING &&
      Date.now() - startTime < MAX_POLL_DURATION_MS &&
      pollAttempts < MAX_POLL_ATTEMPTS
    ) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const statusData = await getArtifactExportStatus(artifactId, exportId);
      currentStatus = statusData.status as ArtifactExportStatus;
      pollAttempts++;
    }

    return currentStatus;
  }, []);

  const download = useCallback(async (): Promise<void> => {
    if (!artifactId) {
      throw new Error('Artifact ID is required');
    }

    setIsDownloading(true);
    setError(null);

    const downloadNotifId = notificationService.showSuccess(
      t('artifacts.useArtifactExport.exportingYourFileThis'),
    );

    try {
      // Step 1: Check for existing export
      const existingExport = await getArtifactExportLast(artifactId, referenceKey);
      let exportId: string;
      let currentStatus: ArtifactExportStatus;

      if (!existingExport) {
        // Step 2: No existing export found — create a new one
        const createBody: CreateArtifactExportDto = {
          ...(templateName && { templateName }),
          ...(referenceKey && { referenceKey }),
          ...(additionalOptions && { additionalOptions }),
        };

        const created = await createArtifactExport(
          artifactId,
          Object.keys(createBody).length > 0 ? createBody : undefined,
        );
        exportId = created.id;
        currentStatus = created.status as ArtifactExportStatus;
      } else {
        exportId = existingExport.id;
        currentStatus = existingExport.status as ArtifactExportStatus;
      }

      // Step 3: Poll if still processing
      if (currentStatus === ArtifactExportStatus.PROCESSING) {
        currentStatus = await pollExport(artifactId, exportId);
      }

      if (currentStatus === ArtifactExportStatus.FAILED) {
        throw new Error(t('artifacts.useArtifactExport.exportGenerationFailedPlease'));
      }

      if (currentStatus !== ArtifactExportStatus.COMPLETED) {
        throw new Error(t('artifacts.useArtifactExport.exportGenerationTimedOut'));
      }

      // Step 4: Get the asset and download
      const asset = await getArtifactExportAsset(artifactId, exportId);
      await downloadAsset(asset.assetId, asset.mimeType, downloadNotifId);
      setIsDownloading(false);
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error(t('artifacts.useArtifactExport.unknownErrorOccurred'), { cause: err });
      setError(error);
      notificationService.close(downloadNotifId);
      notificationService.showError(
        t('artifacts.useArtifactExport.failedToDownloadExport', { message: error.message }),
      );
      setIsDownloading(false);
      throw error;
    }
  }, [artifactId, referenceKey, templateName, additionalOptions, downloadAsset, pollExport, t]);

  return {
    download,
    isDownloading,
    error,
  };
}
